import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Auction } from '../types';
import { AuctionCard } from '../components/AuctionCard';
import { EmptyState } from '../components/EmptyState';
import { useAuth } from '../contexts/AuthContext';

export default function HomePage() {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [participantCounts, setParticipantCounts] = useState<Record<string, number>>({});
  const [notificationPreferences, setNotificationPreferences] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<'open' | 'closed' | 'all'>('open');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('auctions')
      .select('*, creator:profiles!auctions_created_by_fkey(display_name,nickname,avatar_url), winner:profiles!auctions_current_winner_id_fkey(display_name,nickname,avatar_url)')
      .order('status', { ascending: false })
      .order('closes_at', { ascending: true });
    if (error) console.error(error);
    setAuctions((data || []) as Auction[]);

    const [{ data: participants }, { data: preferences }] = await Promise.all([
      supabase.from('auction_participants').select('auction_id'),
      user ? supabase.from('auction_user_preferences').select('auction_id,notifications_enabled').eq('user_id', user.id) : Promise.resolve({ data: [] })
    ]);
    const counts: Record<string, number> = {};
    (participants || []).forEach((row) => { counts[row.auction_id] = (counts[row.auction_id] || 0) + 1; });
    setParticipantCounts(counts);
    const prefs: Record<string, boolean> = {};
    (preferences || []).forEach((row) => { prefs[row.auction_id] = row.notifications_enabled; });
    setNotificationPreferences(prefs);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const channel = supabase.channel('home-auctions')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'auctions' }, load)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bids' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  const toggleNotifications = async (auction: Auction, enabled: boolean) => {
    setNotificationPreferences((current) => ({ ...current, [auction.id]: enabled }));
    const { error } = await supabase.rpc('set_auction_preferences', {
      p_auction_id: auction.id,
      p_notifications_enabled: enabled,
      p_auto_bid_enabled: null,
      p_auto_bid_max: null
    });
    if (error) {
      console.error(error);
      setNotificationPreferences((current) => ({ ...current, [auction.id]: !enabled }));
    }
  };

  const visible = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return auctions.filter((auction) => {
      const matchesFilter = filter === 'all' || (filter === 'open' ? auction.status === 'open' : auction.status === 'closed');
      return matchesFilter && (!normalizedSearch || auction.title.toLowerCase().includes(normalizedSearch));
    });
  }, [auctions, filter, search]);

  return <div className="page-stack">
    <section className="hero-card glass"><div><span className="eyebrow">Gruppo privato</span><h1>Aste temporanee live</h1><p>Rilanci atomici, timer anti-sniping, chat in tempo reale e notifiche push.</p></div><Link to="/auctions/new" className="primary-button"><Plus size={18} /> Nuova asta</Link></section>
    <div className="toolbar-row"><div className="segmented wide"><button className={filter === 'open' ? 'active' : ''} onClick={() => setFilter('open')}>Aperte</button><button className={filter === 'closed' ? 'active' : ''} onClick={() => setFilter('closed')}>Chiuse</button><button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Tutte</button></div><label className="search-box" aria-label="Cerca asta per nome"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca asta per nome…" /></label></div>
    {loading ? <div className="screen-center small">Caricamento aste…</div> : null}
    {!loading && visible.length === 0 ? <EmptyState title={search ? 'Nessuna asta trovata' : 'Nessuna asta ancora'} body={search ? 'Prova con un altro nome.' : 'Crea la prima asta per iniziare.'} action={<Link to="/auctions/new" className="primary-button">Crea asta</Link>} /> : <div className="auction-grid">{visible.map((auction) => <AuctionCard key={auction.id} auction={auction} participantCount={participantCounts[auction.id] || 0} notificationsEnabled={notificationPreferences[auction.id] ?? true} onToggleNotifications={toggleNotifications} />)}</div>}
  </div>;
}
